import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Autocomplete, Tab } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { QUICK_SEARCH_MODAL_FIXED_HEIGHT_PX } from "./config";

const { carbon, blue } = colorsPalettes;

export const Wrapper = styled(Flex)<{ isCollapsed: boolean }>`
    overflow: hidden;
    align-items: center;
    flex-wrap: nowrap;
    width: ${({ isCollapsed }) => (isCollapsed ? "32px" : "100%")};
    padding: ${({ isCollapsed }) => (isCollapsed ? "7px" : "7px 11px")};
    margin-top: 10px;
    margin-left: ${({ isCollapsed }) => (isCollapsed ? -4 : 0)}px;

    border-radius: 4px;
    border: 1px solid ${carbon[50]};

    cursor: pointer;

    transition:
        width 200ms ease-in-out,
        padding 200ms ease-in-out,
        margin-left 200ms ease-in-out,
        background-color 200ms ease-in-out;

    transition-delay: ${({ isCollapsed }) => (isCollapsed ? "250ms" : "0")};

    &:hover {
        background-color: ${blue[50]};
    }
`;

export const Text = styled.span<{ isCollapsed: boolean }>`
    ${setFontVariant("primaryItem", carbon[300])};
    opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
    transition: opacity 200ms ease-in-out;
    transition-delay: ${({ isCollapsed }) => (isCollapsed ? "200ms" : "0")};
    text-wrap: nowrap;
`;

export const SortCutWrapper = styled.div<{ isCollapsed: boolean }>`
    text-align: center;
    padding: 0 6px;

    ${setFontVariant("sectionTitle", carbon[400])};
    font-weight: 700;

    border-radius: 8px;
    background-color: ${blue[50]};
    margin-left: auto;
    text-transform: uppercase;

    opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
    transition: opacity 200ms ease-in-out;
    transition-delay: ${({ isCollapsed }) => (isCollapsed ? "200ms" : "0")};
`;

export const LockIconWrapper = styled(Flex)<{ isCollapsed: boolean }>`
    margin-left: auto;
    align-items: center;
    opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
    transition: opacity 200ms ease-in-out;
    transition-delay: ${({ isCollapsed }) => (isCollapsed ? "200ms" : "0")};
`;

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

export const AutocompleteWrapper = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

export const StyledAutocomplete = styled(Autocomplete)`
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    .AutoCompleteContainer {
        background-color: ${carbon[0]};
        padding: 7px 0;
        transform: translateY(0);
        height: 100%;

        .react-tabs {
            background-color: ${carbon[0]};
        }

        .SWReactIcons[data-automation-icon-name="keywords"] {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: 32px;
            height: 32px;
            border: 1px solid ${carbon[50]};
            border-radius: 6px;
            margin-right: 10px;
            box-sizing: border-box;

            svg {
                width: 16px;
                height: 16px;

                path {
                    fill: ${carbon[400]};
                }
            }
        }

        /* Keep website item favicons at their fixed size; the shared .ItemIcon lacks flex-shrink:0,
           so in the narrow popup a long domain would otherwise squeeze the icon. */
        .ItemIcon {
            flex-shrink: 0;
        }

        [data-automation-item="keywords"] > div:first-child {
            transition: background-color 0.2s ease-out;
            padding: 4px 8px;

            &:hover {
                background-color: ${blue[50]};
            }
        }

        [data-automation="quick-search-modal-tab-keyword-content"] .ScrollArea-content {
            padding: 8px 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        [data-automation="quick-search-modal-tab-website-content"] .ScrollArea-content {
            padding: 8px 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;

            & > div {
                transition: background-color 0.2s ease-out;
                padding: 4px 8px;

                &:hover {
                    background-color: ${blue[50]};
                }
            }
        }

        [data-automation="quick-search-tab-all-websites"] > div:nth-child(2) > div {
            transition: background-color 0.2s ease-out;
            padding: 4px 8px;

            &:hover {
                background-color: ${blue[50]};
            }
        }
    }

    div.AutoCompleteContainer {
        & div[data-automation="quick-search-tab-empty-website"],
        & div[data-automation="quick-search-tab-empty-keyword"],
        & div[data-automation="quick-search-tab-empty-reports"] {
            &:first-child {
                padding-top: 100px;
                pointer-events: none;
            }
        }
    }

    .input-container {
        position: relative;
        padding: 0 16px;
        margin: 1px 0 8px;

        &::after {
            content: "";
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform: translateX(-50%);
            width: calc(100% - 20px);
            height: 1px;
            background-color: ${blue[400]};
        }
    }
`;

export const StyledTab = styled(Tab)`
    height: 48px;
    transition: background-color 0.2s ease-out;

    &:hover {
        background-color: ${blue[50]};
    }
`;

export const StyledTabText = styled.div`
    padding-right: 8px;
`;
